import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'

import { observer } from 'mobx-react'

import CustomFields from 'modules/custom_resources/store/collections/customFields/fields'
import CustomValues from 'modules/custom_resources/store/collections/customFields/values'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type { ValueType } from 'modules/surveys/lib/coerceValueFromType'
import coerceValueFromType from 'modules/surveys/lib/coerceValueFromType'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

import QuestionsWithCategory from '../QuestionsWithCategory'
import QuestionsWithoutCategory from '../QuestionsWithoutCategory'

type QuestionsWithoutCategoryProps = React.ComponentProps<typeof QuestionsWithoutCategory>
type QuestionsWithCategoryProps = React.ComponentProps<typeof QuestionsWithCategory>
interface Props {
  questionGroup: QuestionGroup
}

function ResponseValues({ questionGroup }: Props) {
  const questionGroupWithCategory = questionGroup.withCategory
  const questionGroupWithoutCategory = questionGroup.withoutCategory

  const filterByCategory = React.useRef(
    groupBy(questionGroupWithCategory, (q) => q.surveys_question_category_id)
  ).current

  const uniqueCategories = uniq(
    questionGroupWithCategory.map((q) => q.surveys_question_category_id).sort((a, b) => a - b)
  )

  const createForm = (field: CustomField, value: ValueType) => {
    const values = {
      field_id: field.get('id'),
      field_type: field.get('field_type'),
      mandatory: field.get('required'),
      name: field.get('label'),
      min_value: field.get('min_value'),
      max_value: field.get('max_value'),
      value,
    }

    return new Form(values, {
      field_id: 'number',
      field_type: 'string',
      mandatory: 'boolean',
      name: 'string',
      min_value: 'number',
      max_value: 'number',
      value: 'string',
    })
  }

  // Response without category
  const customFieldsWithoutCategory = React.useRef(
    new CustomFields(
      questionGroupWithoutCategory.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'QuestionGroup',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategory = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategory = React.useMemo(() => {
    return customFieldsWithoutCategory.models.reduce<QuestionsWithoutCategoryProps['options']>(
      (memo, cf) => {
        if (!cf.get('choice_options')) return memo

        memo[cf.get('id')] = cf.get('choice_options')

        return memo
      },
      {}
    )
  }, [customFieldsWithoutCategory])

  const formsWithoutCategory = React.useRef(
    sortBy(customFieldsWithoutCategory.models, (cf: any) => cf.get('position')).map((field) => {
      const answer = customValuesWithoutCategory.find({
        field_id: field.get('id'),
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  const customFieldsWithCategory = React.useRef(
    new CustomFields(
      questionGroupWithCategory.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'QuestionGroup',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithCategory = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithCategory = React.useMemo(() => {
    return customFieldsWithCategory.models.reduce<
      QuestionsWithCategoryProps['optionsWithCategory']
    >((memo, cf) => {
      if (!cf.get('choice_options')) return memo

      memo[cf.get('id')] = cf.get('choice_options')

      return memo
    }, {})
  }, [customFieldsWithCategory])

  const formsWithCategory = React.useRef<Form[]>(
    sortBy(customFieldsWithCategory.models, (cf) => cf.get('position')).map((field) => {
      const answer = customValuesWithCategory.find({
        field_id: field.get('id'),
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  return (
    <>
      <Box gap="s48" padding="s24">
        {formsWithoutCategory.map((form: Form, index: number) => {
          const field = customFieldsWithoutCategory.get(Number(form.get('field_id').value))

          if (!field) {
            return null
          }

          return (
            <QuestionsWithoutCategory
              key={index}
              form={form}
              options={optionsWithoutCategory}
              field={field}
              isResponseValues
            />
          )
        })}
      </Box>
      {uniqueCategories &&
        uniqueCategories.map((categoryId) => {
          const firstFilter: any = filterByCategory[categoryId]?.[0]

          return (
            <QuestionsWithCategory
              key={firstFilter.surveys_question_category.name}
              categoryName={firstFilter.surveys_question_category.name}
              questionCategories={filterByCategory[categoryId] ?? []}
              formsWithCategory={formsWithCategory}
              optionsWithCategory={optionsWithCategory}
              isResponseValues
            />
          )
        })}
    </>
  )
}

export default observer(ResponseValues)
